if (!customElements.get('all-terrain-gallery')) {
  /**
   * Equal-width card gallery. Pad and mobile use native overflow + snap;
   * desktop hides overflow and pages with the previous/next buttons.
   * Vertical page scrolling never changes the active card.
   */
  class AllTerrainGallery extends HTMLElement {
    constructor() {
      super();

      this.viewport = null;
      this.track = null;
      this.cards = [];
      this.frames = [];
      this.videos = [];
      this.previousButton = null;
      this.nextButton = null;
      this.abortController = null;
      this.resizeObserver = null;
      this.videoObserver = null;
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.activeIndex = 0;
      this.programmaticIndex = null;
      this.resizeFrame = 0;
      this.scrollFrame = 0;
      this.scrollEndTimer = null;
      this.observedWidth = 0;
    }

    connectedCallback() {
      if (this.abortController) return;

      this.viewport = this.querySelector('[data-gallery-viewport]');
      this.track = this.querySelector('[data-gallery-track]');
      this.cards = Array.from(this.querySelectorAll('[data-gallery-card]'));
      this.frames = this.cards.map((card) => card.closest('.shopify-block__all-terrain-card') || card);
      this.videos = Array.from(this.querySelectorAll('[data-gallery-video]'));
      this.previousButton = this.querySelector('[data-gallery-previous]');
      this.nextButton = this.querySelector('[data-gallery-next]');

      if (!this.viewport || !this.track || this.cards.length === 0) return;

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.previousButton?.addEventListener('click', () => this.goToIndex(this.activeIndex - 1), { signal });
      this.nextButton?.addEventListener('click', () => this.goToIndex(this.activeIndex + 1), { signal });
      this.addEventListener('keydown', this.handleKeydown, { signal });
      this.viewport.addEventListener('scroll', this.handleTouchScroll, { signal, passive: true });
      this.viewport.addEventListener('pointerdown', this.handlePointerDown, { signal, passive: true });
      this.viewport.addEventListener('wheel', this.handleWheel, { signal, passive: false });

      this.resizeObserver = new ResizeObserver(this.handleResize);
      this.resizeObserver.observe(this);
      this.bindVideoObserver();

      this.track.querySelectorAll('img').forEach((image) => {
        if (image.complete) return;
        image.addEventListener('load', this.handleMediaLoad, { once: true, signal });
      });

      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:block:select', this.handleBlockSelect, { signal });
      }

      this.updateActive(0);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      this.videoObserver?.disconnect();
      this.videoObserver = null;
      window.cancelAnimationFrame(this.resizeFrame);
      window.cancelAnimationFrame(this.scrollFrame);
      window.clearTimeout(this.scrollEndTimer);
      this.pauseVideos();
    }

    /**
     * Uniform stride for equal-width cards: card width + track gap.
     * @returns {number}
     */
    getCardStep() {
      const frame = this.frames[0];
      if (!frame || !this.track) return 0;

      const gap = Number.parseFloat(window.getComputedStyle(this.track).columnGap) || 0;
      return frame.getBoundingClientRect().width + gap;
    }

    /**
     * Scroll offset that left-aligns a card, clamped to the end rail.
     * Later cards can share the same travel once two cards fill the viewport.
     * @param {number} index
     * @returns {number}
     */
    getCardTravel(index) {
      const lastIndex = this.frames.length - 1;
      const maxTravel = this.getMaxTravel();

      if (index <= 0 || lastIndex <= 0) return 0;
      if (index >= lastIndex) return maxTravel;

      return Math.min(maxTravel, index * this.getCardStep());
    }

    /**
     * @param {number} left
     * @param {number} right
     * @returns {boolean}
     */
    isSameTravel(left, right) {
      return Math.abs(left - right) <= 1;
    }

    /**
     * Maps a requested index to one that actually changes scroll position.
     * Going forward at the end rail is a no-op; going backward skips indexes
     * that share the current travel so previous never dead-clicks.
     * @param {number} index
     * @returns {number}
     */
    resolveTargetIndex(index) {
      const lastIndex = this.cards.length - 1;
      const requested = Math.max(0, Math.min(index, lastIndex));
      const requestedTravel = this.getCardTravel(requested);
      const currentTravel = this.getCardTravel(this.activeIndex);

      if (requested === this.activeIndex || !this.isSameTravel(requestedTravel, currentTravel)) {
        return requested;
      }

      if (requested > this.activeIndex) return this.activeIndex;

      for (let cardIndex = this.activeIndex - 1; cardIndex >= 0; cardIndex -= 1) {
        if (!this.isSameTravel(this.getCardTravel(cardIndex), currentTravel)) return cardIndex;
      }

      return 0;
    }

    getMaxTravel() {
      const viewportWidth = this.viewport?.clientWidth || 0;
      return this.track && viewportWidth > 0 ? Math.max(0, this.track.scrollWidth - viewportWidth) : 0;
    }

    handleTouchScroll = () => {
      window.clearTimeout(this.scrollEndTimer);
      this.scrollEndTimer = window.setTimeout(() => {
        window.cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = window.requestAnimationFrame(() => {
          const scrollLeft = this.viewport.scrollLeft;

          if (this.programmaticIndex !== null) {
            const targetIndex = this.programmaticIndex;
            if (Math.abs(this.getCardTravel(targetIndex) - scrollLeft) <= 1) {
              this.programmaticIndex = null;
              this.updateActive(targetIndex);
            }
            return;
          }

          let nearestIndex = 0;
          let nearestDistance = Number.POSITIVE_INFINITY;

          this.frames.forEach((_, index) => {
            const distance = Math.abs(this.getCardTravel(index) - scrollLeft);
            if (distance < nearestDistance) {
              nearestDistance = distance;
              nearestIndex = index;
              return;
            }

            if (this.isSameTravel(distance, nearestDistance) && index > nearestIndex) {
              nearestIndex = index;
            }
          });

          this.updateActive(nearestIndex);
        });
      }, 80);
    };

    handlePointerDown = () => {
      this.programmaticIndex = null;
    };

    handleWheel = (event) => {
      if (event.shiftKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) event.preventDefault();
    };

    handleResize = (entries) => {
      const width = Math.round(entries[0]?.contentRect.width || this.clientWidth);
      if (width === this.observedWidth) return;
      this.observedWidth = width;

      window.cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = window.requestAnimationFrame(() => this.goToIndex(this.activeIndex, false));
    };

    handleMediaLoad = () => {
      window.cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = window.requestAnimationFrame(() => this.goToIndex(this.activeIndex, false));
    };

    updateActive(index) {
      const lastIndex = this.cards.length - 1;
      this.activeIndex = Math.max(0, Math.min(index, lastIndex));

      this.cards.forEach((card, cardIndex) => {
        card.classList.toggle('is-active', cardIndex === this.activeIndex);
      });

      if (this.previousButton) this.previousButton.disabled = this.activeIndex <= 0;
      if (this.nextButton) {
        this.nextButton.disabled = this.isSameTravel(
          this.getCardTravel(this.activeIndex + 1),
          this.getCardTravel(this.activeIndex)
        );
      }
    }

    goToIndex(index, animate = true) {
      const targetIndex = this.resolveTargetIndex(index);
      if (animate && targetIndex === this.activeIndex) return;
      const shouldAnimate = animate && !this.reducedMotionQuery.matches;

      this.programmaticIndex = shouldAnimate ? targetIndex : null;

      this.viewport.scrollTo({
        left: this.getCardTravel(targetIndex),
        behavior: shouldAnimate ? 'smooth' : 'auto',
      });
      this.updateActive(targetIndex);
    }

    handleKeydown = (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;

      event.preventDefault();
      this.goToIndex(this.activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
    };

    handleBlockSelect = (event) => {
      if (!(event.target instanceof Element) || !this.contains(event.target)) return;

      const card = event.target.matches('[data-gallery-card]')
        ? event.target
        : event.target.querySelector('[data-gallery-card]');
      const index = card ? this.cards.indexOf(card) : -1;
      if (index >= 0) this.goToIndex(index, false);
    };

    bindVideoObserver() {
      if (!this.videos.length || !('IntersectionObserver' in window)) return;

      this.videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            if (!(video instanceof HTMLVideoElement)) return;

            if (entry.isIntersecting) {
              const playResult = video.play();
              if (playResult && typeof playResult.catch === 'function') playResult.catch(() => {});
              return;
            }

            video.pause();
          });
        },
        { root: this.viewport, threshold: 0.35 }
      );

      this.videos.forEach((video) => this.videoObserver.observe(video));
    }

    pauseVideos() {
      this.videos.forEach((video) => {
        if (video instanceof HTMLVideoElement) video.pause();
      });
    }
  }

  customElements.define('all-terrain-gallery', AllTerrainGallery);
}
